import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';

import { cn } from '@/hen-ui/utils';
import { CircleIcon } from 'lucide-react';

const RadioItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    data-slot='radio-item'
    className={cn(
      // Base styles
      'flex size-4 shrink-0 rounded-full border aspect-square relative',
      'peer group/radio-item outline-none',
      // Default state
      'border-neutral-200 text-primary-700',
      // Focus state (WCAG 2.1 AA)
      'focus:border-neutral-500 focus:ring focus:ring-neutral-100 hover:border-neutral-500 hover:ring hover:ring-neutral-100',
      // Invalid/error state (static class, correct syntax)
      'aria-[invalid=true]:border-error-500 aria-[invalid=true]:focus:ring aria-[invalid=true]:focus:ring-error-300 aria-[invalid=true]:hover:ring aria-[invalid=true]:hover:ring-error-300',
      // Disabled state
      'disabled:cursor-not-allowed disabled:opacity-50 disabled:ring-0 disabled:hover:border-neutral-200',
      // Clickable area
      'after:absolute after:-inset-x-3 after:-inset-y-2',
      className,
    )}
    {...props}
  >
    <RadioGroupPrimitive.Indicator
      data-slot='radio-group-indicator'
      className='flex size-4 items-center justify-center'
    >
      <CircleIcon className='absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2 fill-primary-700 group-aria-[invalid=true]/radio-item:fill-error-500 stroke-none' />
    </RadioGroupPrimitive.Indicator>
  </RadioGroupPrimitive.Item>
));
RadioItem.displayName = RadioGroupPrimitive.Item.displayName;

const Radio = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    ref={ref}
    data-slot='radio-group'
    className={cn(
      className,
      'flex gap-md',
      'aria-[orientation=vertical]:flex-col',
      'aria-[orientation=horizontal]:flex-row aria-[orientation=horizontal]:flex-wrap',
      className,
    )}
    {...props}
  />
));
Radio.displayName = RadioGroupPrimitive.Root.displayName;

export { Radio, RadioItem };
